import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Image,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Paragraph,
  Radio,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import { GeneratedLeaseDeclineReason, LeaseArtifactType, LeaseType } from '@zg-rentals/ts-leasing-client';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import { Lease, Property } from '../../../models';
import { useLeasingUrlParams } from '../../../hooks';
import {
  useArchiveLeaseMutation,
  useCreateNewLeaseMutation,
  usePropertyInfoQuery,
  useSaveGeneratedLeaseFeedbackMutation,
  useWebAnalyticsForListingQuery,
} from '../../../utils/queries';
import DocumentSignature from '../../../../../images/documentSignature.svg';
import Folder from '../../../../../images/folder.svg';
import { emitFeedbackModalSubmitEvent } from '../../../../analytics/events';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const MAX_OTHER_REASON_LENGTH = 500;

const FEEDBACK_OPTIONS = [
  { value: GeneratedLeaseDeclineReason.ALREADY_SIGNED, label: 'I’ve already signed a lease' },
  { value: GeneratedLeaseDeclineReason.NEED_MORE_TIME, label: 'I need more time to review the lease' },
  { value: GeneratedLeaseDeclineReason.NOT_READY_TO_SIGN, label: 'I’m not ready to sign yet' },
  { value: GeneratedLeaseDeclineReason.USE_MY_OWN_LEASE, label: 'I prefer to use my own lease' },
  { value: GeneratedLeaseDeclineReason.DIDNT_UNDERSTAND, label: 'I didn’t understand some parts of the lease' },
  { value: GeneratedLeaseDeclineReason.OTHER, label: 'Other' },
];

export function FeedbackModal({ isOpen, onClose }: Props) {
  const history = useHistory();
  const { leaseId, propertyId } = useLeasingUrlParams();
  const [selectedReason, setSelectedReason] = useState<GeneratedLeaseDeclineReason | null>(null);
  const [otherReasonText, setOtherReasonText] = useState('');
  const [otherReasonFocused, setOtherReasonFocused] = useState(false);
  const [showUploadSignedLeaseView, setShowUploadSignedLeaseView] = useState(false);
  const [showUploadOwnLeaseView, setShowUploadOwnLeaseView] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const { user } = useRentalManagerUser();

  const propertyQuery = usePropertyInfoQuery(propertyId, !!propertyId);
  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const property = propertyQuery.data ? new Property(propertyQuery.data) : undefined;

  const createNewLeaseMutation = useCreateNewLeaseMutation();
  const archiveLeaseMutation = useArchiveLeaseMutation();
  const saveFeedbackMutation = useSaveGeneratedLeaseFeedbackMutation();

  async function handleSubmit() {
    if (!selectedReason) return;

    await saveFeedbackMutation.mutateAsync({
      postData: {
        declineReason: selectedReason,
        leaseId: leaseId as unknown as number,
        listingAlias: property?.listingAlias || propertyId,
        otherReason: selectedReason === GeneratedLeaseDeclineReason.OTHER ? otherReasonText : '',
      },
    });

    if (selectedReason === GeneratedLeaseDeclineReason.ALREADY_SIGNED) {
      setShowUploadSignedLeaseView(true);
    } else if (selectedReason === GeneratedLeaseDeclineReason.USE_MY_OWN_LEASE) {
      setShowUploadOwnLeaseView(true);
    } else if (selectedReason) {
      setIsNavigating(true);
      await archiveLeaseMutation.mutateAsync({
        postData: { leaseArtifactType: LeaseArtifactType.LEASE, leaseArtifactId: leaseId as unknown as number },
      });
      window.location.href = Property.getPropertyDashboardOverviewPath(property.listingAlias);
    }
  }

  function handleClose() {
    setSelectedReason(null);
    setOtherReasonText('');
    setShowUploadSignedLeaseView(false);
    setShowUploadOwnLeaseView(false);
    onClose();
  }

  async function handleSignedLeaseNoThanks() {
    emitFeedbackModalSubmitEvent({ user, listing: webAnalyticsQuery.data });
    setIsNavigating(true);
    await archiveLeaseMutation.mutateAsync({
      postData: { leaseArtifactType: LeaseArtifactType.LEASE, leaseArtifactId: leaseId as unknown as number },
    });
    window.location.href = Property.getPropertyDashboardOverviewPath(property.listingAlias);
  }

  async function handleSignedLeaseUploadClose() {
    setIsNavigating(true);
    await archiveLeaseMutation.mutateAsync({
      postData: { leaseArtifactType: LeaseArtifactType.LEASE, leaseArtifactId: leaseId as unknown as number },
    });
    window.location.href = Property.getPropertyDashboardOverviewPath(property.listingAlias);
  }

  async function handleUploadSignedLease() {
    await archiveLeaseMutation.mutateAsync({
      postData: { leaseArtifactType: LeaseArtifactType.LEASE, leaseArtifactId: leaseId as unknown as number },
    });

    const response = await createNewLeaseMutation.mutateAsync({
      postData: {
        propertyId,
        leaseType: LeaseType.SIGNED_SELF_UPLOAD,
      },
    });

    if (response?.leaseId) {
      history.push(Lease.getSelfServePath(String(response.leaseId)));
    }
  }

  async function handleOwnLeaseNoThanks() {
    emitFeedbackModalSubmitEvent({ user, listing: webAnalyticsQuery.data });
    setIsNavigating(true);
    await archiveLeaseMutation.mutateAsync({
      postData: { leaseArtifactType: LeaseArtifactType.LEASE, leaseArtifactId: leaseId as unknown as number },
    });
    window.location.href = Property.getPropertyDashboardOverviewPath(property.listingAlias);
  }

  async function handleOwnLeaseUploadClose() {
    setIsNavigating(true);
    await archiveLeaseMutation.mutateAsync({
      postData: { leaseArtifactType: LeaseArtifactType.LEASE, leaseArtifactId: leaseId as unknown as number },
    });
    window.location.href = Property.getPropertyDashboardOverviewPath(property.listingAlias);
  }

  async function handleUploadOwnLease() {
    await archiveLeaseMutation.mutateAsync({
      postData: { leaseArtifactType: LeaseArtifactType.LEASE, leaseArtifactId: leaseId as unknown as number },
    });

    const response = await createNewLeaseMutation.mutateAsync({
      postData: {
        propertyId,
        leaseType: LeaseType.SELF_UPLOAD,
      },
    });

    if (response?.leaseId) {
      history.push(Lease.getSelfServePath(String(response.leaseId)));
    }
  }

  const isSubmitDisabled =
    !selectedReason ||
    (selectedReason === GeneratedLeaseDeclineReason.OTHER && !otherReasonText.trim()) ||
    archiveLeaseMutation.isPending ||
    isNavigating;

  const isUploadViewLoading = archiveLeaseMutation.isPending || createNewLeaseMutation.isPending || isNavigating;

  if (showUploadSignedLeaseView) {
    return (
      <Modal
        size={{ base: 'fullScreen', md: 'sm' }}
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleSignedLeaseUploadClose();
          }
        }}
        header={<Heading level={1}>Lease management</Heading>}
        useDismissProps={{ escapeKey: false, outsidePress: false }}
        body={
          <>
            <Image
              src={Folder}
              alt="manila folder with paper inside"
              css={{ height: '160px', width: '160px', marginBottom: 'tight' }}
            />
            <Heading level={2} css={{ textStyle: 'responsive.heading-xs', marginBottom: 'tight' }}>
              Upload your signed lease
            </Heading>
            <Paragraph>
              Protect yourself by storing digital backup of your lease agreement with Zillow. We’ll also send you a
              reminder before your lease ends so you can renew or update it.
            </Paragraph>
          </>
        }
        footer={
          <ButtonGroup aria-label="upload signed lease actions">
            <TextButton tone="brand" onClick={handleSignedLeaseNoThanks} disabled={isUploadViewLoading}>
              No thanks
            </TextButton>
            <Button
              tone="brand"
              emphasis="filled"
              loading={isUploadViewLoading}
              onClick={handleUploadSignedLease}
              disabled={isUploadViewLoading}
            >
              Upload lease
            </Button>
          </ButtonGroup>
        }
      />
    );
  }

  if (showUploadOwnLeaseView) {
    return (
      <Modal
        size={{ base: 'fullScreen', md: 'sm' }}
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleOwnLeaseUploadClose();
          }
        }}
        useDismissProps={{ escapeKey: false, outsidePress: false }}
        header={<Heading level={1}>Lease management</Heading>}
        body={
          <>
            <Image
              src={DocumentSignature}
              alt="document with a pen and signature"
              css={{ height: '160px', width: '160px', marginBottom: 'tight' }}
            />
            <Heading level={2} css={{ textStyle: 'responsive.heading-xs', marginBottom: 'tight' }}>
              Upload your lease for signing
            </Heading>
            <Paragraph>
              We’ll help you complete your lease agreement and share it with your renter to sign. The signed lease will
              be stored on Zillow for quick access. We’ll also send you a reminder before your lease term ends so you
              can renew or update it.
            </Paragraph>
          </>
        }
        footer={
          <ButtonGroup aria-label="upload own lease actions">
            <TextButton tone="brand" onClick={handleOwnLeaseNoThanks} disabled={isUploadViewLoading}>
              No thanks
            </TextButton>
            <Button tone="brand" emphasis="filled" onClick={handleUploadOwnLease} disabled={isUploadViewLoading}>
              Upload lease
            </Button>
          </ButtonGroup>
        }
      />
    );
  }

  return (
    <Modal
      size={{ base: 'fullScreen', md: 'sm' }}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
      header={<Heading level={1}>Help us understand your decision</Heading>}
      body={
        <>
          <Paragraph css={{ marginBottom: 'loose' }}>
            We noticed you chose not to complete this lease. Your feedback will help us improve.
          </Paragraph>
          <FieldSet required legend={<Legend>What’s the main reason you didn’t move forward with this lease?</Legend>}>
            {FEEDBACK_OPTIONS.map((option) => (
              <LabeledControl
                key={option.value}
                label={<Label>{option.label}</Label>}
                control={
                  <Radio
                    name="feedback-reason"
                    value={option.value}
                    checked={selectedReason === option.value}
                    onChange={() => setSelectedReason(option.value)}
                  />
                }
              />
            ))}
          </FieldSet>
          {selectedReason === GeneratedLeaseDeclineReason.OTHER && (
            <FormField
              required
              css={{ marginTop: 'tighter' }}
              label={<Label>Other reason</Label>}
              error={otherReasonText.length > MAX_OTHER_REASON_LENGTH}
              control={
                <Textarea
                  css={{ resize: 'vertical' }}
                  value={otherReasonText}
                  onChange={(e) => setOtherReasonText(e.target.value)}
                  onFocus={() => setOtherReasonFocused(true)}
                  onBlur={() => setOtherReasonFocused(false)}
                  rows={3}
                />
              }
              description={
                <FormHelp
                  css={
                    otherReasonFocused || otherReasonText.length > MAX_OTHER_REASON_LENGTH
                      ? { display: 'flex' }
                      : undefined
                  }
                >
                  <span className={css({ flex: 1 })}>
                    {otherReasonText.length > MAX_OTHER_REASON_LENGTH
                      ? `Reason must be ${MAX_OTHER_REASON_LENGTH} characters or fewer`
                      : ''}
                  </span>
                  {(otherReasonFocused || otherReasonText.length > MAX_OTHER_REASON_LENGTH) && (
                    <span
                      className={css({
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        marginLeft: 'auto',
                        paddingLeft: 'tighter',
                      })}
                    >
                      {otherReasonText.length.toLocaleString()}/{MAX_OTHER_REASON_LENGTH} characters
                    </span>
                  )}
                </FormHelp>
              }
            />
          )}
        </>
      }
      footer={
        <ButtonGroup aria-label="feedback actions">
          <TextButton tone="brand" onClick={handleClose} disabled={archiveLeaseMutation.isPending || isNavigating}>
            Close
          </TextButton>
          <Button tone="brand" emphasis="filled" onClick={handleSubmit} disabled={isSubmitDisabled}>
            Submit feedback
          </Button>
        </ButtonGroup>
      }
    />
  );
}

export default FeedbackModal;
